import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import {
  FORM_DATA_STEP_PREFERENCES,
  listsTicketMaxPref,
  listsTicketMinPref,
  SignInTicketOption,
} from '../../data/data';

type ZoneOption = (typeof FORM_DATA_STEP_PREFERENCES.modules)[0]['options'][number];
type AssetOption = (typeof FORM_DATA_STEP_PREFERENCES.modulesMultiple)[0]['options'][number];

function zoneOptionRequiresText(opt: ZoneOption | undefined): boolean {
  return !!opt && 'input' in opt && !!opt.input;
}

@Component({
  selector: 'app-sign-in-step-preferences',
  templateUrl: './sign-in-step-preferences.component.html',
  styleUrls: ['./sign-in-step-preferences.component.scss'],
})
export class SignInStepPreferencesComponent
  implements OnInit, OnChanges, OnDestroy
{
  readonly formData = FORM_DATA_STEP_PREFERENCES;

  /** Misma longitud y orden que `modulesMultiple[0].options`; claves internas del padre. */
  private readonly assetToggleKeys = ['offices', 'big', 'newbuild', 'other'] as const;

  @Input() registerForm!: FormGroup;
  @Input() prefZones = '';
  @Input() prefSelectedZone = '';
  @Input() prefMinTicket = '';
  @Input() prefMaxTicket = '';
  @Input() prefAssetTypes: string[] = [];
  @Input() prefPropertiesCount = '';
  @Input() prefProblematic = false;
  @Input() isLoading = false;

  @Output() prefZonesChange = new EventEmitter<string>();
  @Output() prefSelectedZoneChange = new EventEmitter<string>();
  @Output() prefMinTicketChange = new EventEmitter<string>();
  @Output() prefMaxTicketChange = new EventEmitter<string>();
  @Output() prefPropertiesCountChange = new EventEmitter<string>();
  @Output() prefProblematicChange = new EventEmitter<boolean>();
  @Output() toggleAssetType = new EventEmitter<string>();
  @Output() submit = new EventEmitter<void>();

  arrayTicketMin: SignInTicketOption[] = [];
  arrayTicketMax: SignInTicketOption[] = [];

  private langSub?: Subscription;

  constructor(
    public translateService: TranslateService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.arrayTicketMin = listsTicketMinPref.map((o) => ({ ...o }));
    this.arrayTicketMax = listsTicketMaxPref.map((o) => ({ ...o }));
    this.syncDisabledStates();
    this.langSub = this.translateService.onLangChange.subscribe(() =>
      this.cdr.markForCheck(),
    );
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes['prefMinTicket'] || changes['prefMaxTicket']) &&
      this.arrayTicketMin.length &&
      this.arrayTicketMax.length
    ) {
      this.syncDisabledStates();
    }
  }

  isSpanish(): boolean {
    return this.translateService.getCurrentLang() === 'es';
  }

  headerTitle(): string {
    return this.isSpanish() ? this.formData.title : this.formData.title_en;
  }

  headerSubtitle(): string {
    return this.isSpanish() ? this.formData.subtitle : this.formData.subtitle_en;
  }

  buttonLabel(): string {
    return this.isSpanish()
      ? this.formData.buttonText
      : this.formData.buttonText_en;
  }

  ticketSectionTitle(): string {
    const t = this.formData.ticketModules;
    return this.isSpanish() ? t.title : t.title_en;
  }

  selectGroupLabel(
    g: (typeof this.formData.ticketModules.selectGroup)[0],
  ): string {
    return this.isSpanish() ? g.label : g.label_en;
  }

  /** Placeholder del desplegable de ticket (solo `placeholder` en data; EN genérico). */
  ticketPlaceholder(
    g: (typeof this.formData.ticketModules.selectGroup)[0],
  ): string {
    if (this.isSpanish()) {
      return g.placeholder;
    }
    return 'Select';
  }

  zoneModuleLabel(): string {
    const m = this.formData.modules[0];
    return this.isSpanish() ? m.label : m.label_en;
  }

  zoneOptionLabel(opt: ZoneOption): string {
    const raw = this.isSpanish() ? opt.label : opt.label_en;
    return raw.trim();
  }

  zoneInputPlaceholder(opt: ZoneOption): string {
    if (!('inputPlaceholder' in opt) || !opt.input) {
      return '';
    }
    return this.isSpanish()
      ? (opt.inputPlaceholder ?? '')
      : (opt.inputPlaceholder_en ?? opt.inputPlaceholder ?? '');
  }

  assetModuleLabel(): string {
    const m = this.formData.modulesMultiple[0];
    return this.isSpanish() ? m.label : m.label_en;
  }

  assetOptionLabel(opt: AssetOption): string {
    return this.isSpanish() ? opt.label : opt.label_en;
  }

  propertiesTitle(): string {
    const t = this.formData.modulesOptions;
    return this.isSpanish() ? t.title : t.title_en;
  }

  propertyOptionLabel(
    opt: (typeof this.formData.modulesOptions.options)[0],
  ): string {
    return this.isSpanish() ? opt.label : opt.label_en;
  }

  problematicLabel(): string {
    const c = this.formData.modulesCheckbox;
    return this.isSpanish() ? c.title : c.title_en;
  }

  onZoneOptionChange(opt: ZoneOption): void {
    this.prefSelectedZoneChange.emit(opt.value);
    if (!opt.input) {
      this.prefZonesChange.emit('');
    }
  }

  assetToggleKey(index: number): string {
    return this.assetToggleKeys[index] ?? 'other';
  }

  getSelectedMinTicket(): string {
    if (this.prefMinTicket === '' || this.prefMinTicket == null) {
      return '';
    }
    const found = this.arrayTicketMin.find(
      (o) => o.value === this.prefMinTicket,
    );
    return found ? found.name : '';
  }

  getSelectedMaxTicket(): string {
    if (this.prefMaxTicket === '' || this.prefMaxTicket == null) {
      return '';
    }
    const found = this.arrayTicketMax.find(
      (o) => o.value === this.prefMaxTicket,
    );
    return found ? found.name : '';
  }

  onTicketChange(event: MatSelectChange, operacion: 'desde' | 'hasta'): void {
    const raw = (event.value as string) ?? '';
    const numeric = Number(raw);

    if (operacion === 'desde') {
      this.prefMinTicketChange.emit(raw);
      this.updateMinTicket(numeric);
    } else {
      this.prefMaxTicketChange.emit(raw);
      this.updateMaxTicket(numeric);
    }
  }

  private updateMinTicket(value: number): void {
    this.arrayTicketMax.forEach((el) => {
      el.isDisabled = Number(el.value) <= value;
    });
  }

  private updateMaxTicket(value: number): void {
    this.arrayTicketMin.forEach((el) => {
      el.isDisabled = Number(el.value) >= value;
    });
  }

  /**
   * Paso de preferencias completo: zona, texto de zona si la opción lo pide, tickets y recuento de inmuebles.
   * (Los tipos de activo adicionales pueden quedar vacíos: equivale a solo residencial.)
   */
  preferencesStepComplete(): boolean {
    const zone = (this.prefSelectedZone ?? '').trim();
    if (!zone) {
      return false;
    }

    const zoneOpt = this.formData.modules[0].options.find((o) => o.value === this.prefSelectedZone);
    if (zoneOptionRequiresText(zoneOpt) && !(this.prefZones ?? '').trim()) {
      return false;
    }

    if (!(this.prefMinTicket ?? '').trim() || !(this.prefMaxTicket ?? '').trim()) {
      return false;
    }

    if (!(this.prefPropertiesCount ?? '').trim()) {
      return false;
    }

    return true;
  }

  private syncDisabledStates(): void {
    const minVal =
      this.prefMinTicket === '' || this.prefMinTicket == null
        ? 0
        : Number(this.prefMinTicket);
    const maxVal =
      this.prefMaxTicket === '' || this.prefMaxTicket == null
        ? Number.POSITIVE_INFINITY
        : Number(this.prefMaxTicket);

    this.arrayTicketMax.forEach((el) => {
      el.isDisabled = Number(el.value) <= minVal;
    });
    this.arrayTicketMin.forEach((el) => {
      el.isDisabled = Number(el.value) >= maxVal;
    });
  }
}
